import React, { useState, useMemo, useEffect, useRef } from "react";

const LANGUAGES = [
  { code: "en", native: "English",          english: "English",     sample: "Hello" },
  { code: "zh", native: "中文",              english: "Chinese",     sample: "你好" },
  { code: "es", native: "Español",           english: "Spanish",     sample: "Hola" },
  { code: "fr", native: "Français",          english: "French",      sample: "Bonjour" },
  { code: "de", native: "Deutsch",           english: "German",      sample: "Hallo" },
  { code: "hi", native: "हिन्दी",             english: "Hindi",       sample: "नमस्ते" },
  { code: "id", native: "Bahasa Indonesia",  english: "Indonesian",  sample: "Halo" },
  { code: "it", native: "Italiano",          english: "Italian",     sample: "Ciao" },
  { code: "ja", native: "日本語",             english: "Japanese",    sample: "こんにちは" },
  { code: "ko", native: "한국어",             english: "Korean",      sample: "안녕하세요" },
  { code: "pt", native: "Português",         english: "Portuguese",  sample: "Olá" },
  { code: "ru", native: "Русский",           english: "Russian",     sample: "Привет" },
  { code: "vi", native: "Tiếng Việt",        english: "Vietnamese",  sample: "Xin chào" },
];

const LANG_BY_CODE = Object.fromEntries(LANGUAGES.map((l) => [l.code, l]));

const CHIP_PALETTE = {
  en: ["#dbeafe", "#1d4ed8"], zh: ["#fee2e2", "#b91c1c"], es: ["#fef3c7", "#b45309"],
  fr: ["#dbeafe", "#1e40af"], de: ["#fde68a", "#854d0e"], hi: ["#ffedd5", "#c2410c"],
  id: ["#fee2e2", "#991b1b"], it: ["#dcfce7", "#166534"], ja: ["#fee2e2", "#991b1b"],
  ko: ["#e0e7ff", "#3730a3"], pt: ["#dcfce7", "#15803d"], ru: ["#dbeafe", "#1e3a8a"],
  vi: ["#fee2e2", "#b91c1c"],
};

function CodeChip({ code }) {
  const [bg, fg] = CHIP_PALETTE[code] || ["#e0f2fe", "#0369a1"];
  return (
    <span
      style={{ background: bg, color: fg }}
      className="inline-flex items-center justify-center min-w-[28px] h-[22px] px-1.5 rounded-md font-mono text-[11px] font-semibold uppercase tracking-wide shrink-0"
    >
      {code}
    </span>
  );
}

function LanguageDropdown({ value, onChange, disabled }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [focusIdx, setFocusIdx] = useState(0);
  const wrapRef = useRef(null);
  const searchRef = useRef(null);

  const selected = LANG_BY_CODE[value] || LANG_BY_CODE.en;

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return LANGUAGES;
    return LANGUAGES.filter(
      (l) =>
        l.code.includes(q) ||
        l.native.toLowerCase().includes(q) ||
        l.english.toLowerCase().includes(q)
    );
  }, [query]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
      if (e.key === "ArrowDown") { e.preventDefault(); setFocusIdx((i) => Math.min(i + 1, filtered.length - 1)); }
      if (e.key === "ArrowUp")   { e.preventDefault(); setFocusIdx((i) => Math.max(i - 1, 0)); }
      if (e.key === "Enter")     { e.preventDefault(); const l = filtered[focusIdx]; if (l) { onChange(l.code); setOpen(false); } }
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    setTimeout(() => searchRef.current?.focus(), 30);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, filtered, focusIdx, onChange]);

  useEffect(() => { setFocusIdx(0); }, [query, open]);

  return (
    <div ref={wrapRef} className="relative">
      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setOpen((o) => !o)}
        className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl border border-[rgba(15,23,42,0.08)] bg-white text-left transition ${
          disabled ? "opacity-50 cursor-not-allowed" : "hover:border-[#3AB1F6]"
        }`}
      >
        <CodeChip code={selected.code} />
        <div className="flex flex-col flex-1 min-w-0">
          <span className="text-sm font-medium text-gray-900 leading-tight truncate">{selected.native}</span>
          {selected.native !== selected.english && (
            <span className="text-[11px] text-gray-400 leading-tight">{selected.english}</span>
          )}
        </div>
        <svg
          width="14" height="14" viewBox="0 0 16 16" fill="none"
          style={{ transform: open ? "rotate(180deg)" : "none", transition: "transform .18s" }}
          className="text-gray-400 shrink-0"
        >
          <path d="M4 6l4 4 4-4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {open && (
        <div className="absolute top-[calc(100%+6px)] left-0 right-0 bg-white border border-[rgba(15,23,42,0.08)] rounded-xl shadow-xl z-30 overflow-hidden">
          {/* Search */}
          <div className="p-2 border-b border-[rgba(15,23,42,0.06)]">
            <div className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg bg-[rgba(15,23,42,0.04)] text-gray-400">
              <svg width="13" height="13" viewBox="0 0 16 16" fill="none">
                <circle cx="7" cy="7" r="4.5" stroke="currentColor" strokeWidth="1.5" />
                <path d="M10.5 10.5L13.5 13.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
              <input
                ref={searchRef}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search languages…"
                className="flex-1 min-w-0 bg-transparent border-0 outline-none text-[13px] text-gray-900 font-inter"
              />
              {query && (
                <button onClick={() => setQuery("")} className="text-gray-400 hover:text-gray-600 leading-none text-sm">×</button>
              )}
            </div>
          </div>

          {/* List */}
          <div className="max-h-[260px] overflow-y-auto p-1">
            {filtered.length === 0 && (
              <div className="py-4 text-center text-xs text-gray-400">No matches</div>
            )}
            {filtered.map((lang, i) => {
              const isSel = lang.code === value;
              const isFoc = i === focusIdx;
              return (
                <div
                  key={lang.code}
                  role="option"
                  aria-selected={isSel}
                  onMouseEnter={() => setFocusIdx(i)}
                  onClick={() => { onChange(lang.code); setOpen(false); }}
                  className={`flex items-center gap-2.5 px-2.5 py-2 rounded-lg cursor-pointer transition-colors ${
                    isFoc ? "bg-[rgba(59,130,246,0.08)]" : ""
                  }`}
                >
                  <CodeChip code={lang.code} />
                  <div className="flex-1 min-w-0">
                    <div className="text-[13px] font-medium text-gray-900 leading-tight">{lang.native}</div>
                    {lang.native !== lang.english && (
                      <div className="text-[11px] text-gray-400 leading-tight">{lang.english}</div>
                    )}
                  </div>
                  <span className="text-[11px] text-gray-300 italic truncate max-w-[80px]">{lang.sample}</span>
                  <span className={`w-4 shrink-0 ${isSel ? "text-blue-600" : "text-transparent"}`}>
                    <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                      <path d="M3.5 8.5L6.5 11.5L12.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </span>
                </div>
              );
            })}
          </div>

          {/* Footer */}
          <div className="px-3 py-1.5 border-t border-[rgba(15,23,42,0.06)] flex justify-between items-center text-[10.5px] text-gray-400 bg-[rgba(15,23,42,0.015)]">
            <span>{filtered.length} of {LANGUAGES.length} languages</span>
          </div>
        </div>
      )}
    </div>
  );
}

const OutputLanguageCard = ({ value, onChange, disabled = false, className = "" }) => {
  return (
    <div className={`rounded-2xl border border-[#D6ECFB] bg-white p-4 ${className}`}>
      <div className="shrink-0 flex items-center gap-3 md:text-[1.75rem] text-xl font-semibold font-inter">
        <span className="bg-gradient-to-b from-[#3AB1F6] to-[#407BFF] text-white p-1.5 rounded-lg">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.7" />
            <path d="M3 12h18M12 3c2.5 3 3.5 6 3.5 9s-1 6-3.5 9M12 3c-2.5 3-3.5 6-3.5 9s1 6 3.5 9" stroke="currentColor" strokeWidth="1.7" />
          </svg>
        </span>
        Output Language
        <span className="ml-auto text-sm font-normal text-gray-400">{LANGUAGES.length} available</span>
      </div>

      <div className="mt-3">
        <LanguageDropdown value={value} onChange={onChange} disabled={disabled} />
      </div>

      <p className="mt-2 text-xs text-gray-400 font-inter">
        Your transcript will be translated into the selected language.
      </p>
    </div>
  );
};

export { LANGUAGES, LANG_BY_CODE };
export default OutputLanguageCard;
